import { Link } from "@/lib/router-compat";
import { Bell, Scale, Bed, LayoutGrid } from "lucide-react";
import { useContext, useEffect, useRef, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import CardImage from "@/components/inventory/CardImage";
import GetLowestPrice from "@/components/inventory/GetLowestPrice";
import { ActionForm } from "@/components/inventory/UnitActions";
import { DollarSign } from "lucide-react";
import WishlistButton from "@/components/inventory/WishlistButton";
import AdminEditButton from "@/components/inventory/AdminEditButton";
import { PLACEHOLDER_URL, renderUrl } from "@/lib/imageUtils";
import ResilientImg from "@/components/ui/ResilientImg";
import { useAutoCycleSetting } from "@/components/inventory/RVCard";
import type { RVUnit } from "@/hooks/useInventory";
import { rvPlaceholder } from "@/lib/rvPlaceholders";
import { unitPhotos } from "@/lib/rvPhotoPolicy";
function fmt(n: number | null | undefined) {
  return n ? n.toLocaleString() : "—";
}

interface Props {
  rv: RVUnit;
  priority?: boolean;
}

/**
 * Wide horizontal row card: image (left) | spec column (middle) | pricing + CTAs (right).
 * Matches the reference layout used on Inventory list view.
 */
export default function HorizontalRowCard({ rv, priority }: Props) {
  const phone = "910-793-2894";
  const floorplanSrc = rv.floorplan_image ? renderUrl(rv.floorplan_image, 400) : "";
  const displayPrice = rv.sale_price ?? rv.price;
  const savings =
    rv.price && rv.sale_price && rv.sale_price < rv.price ? rv.price - rv.sale_price : 0;
  const isSold = rv.status === "sold";

  // Image carousel cycling (mirrors RVCard behavior)
  const fallback = rvPlaceholder(rv.rv_type);
  const photos = unitPhotos(rv.images);
  const baseImages = photos.length ? photos : [fallback];
  const rawImages = rv.floorplan_image
    ? [baseImages[0] ?? fallback, rv.floorplan_image, ...baseImages.slice(1).filter((u) => u !== rv.floorplan_image)]
    : baseImages;
  const { data: autoCycle } = useAutoCycleSetting();
  const [imgIdx, setImgIdx] = useState(0);
  const [failedUrls, setFailedUrls] = useState<Set<string>>(new Set());
  const timerRef = useRef<ReturnType<typeof setInterval> | undefined>(undefined);
  const images = rawImages.filter((u) => !failedUrls.has(u));
  const hasFloorplan = !!rv.floorplan_image && images.includes(rv.floorplan_image);
  const shouldCycle = (autoCycle?.enabled || hasFloorplan) && images.length > 1;

  useEffect(() => {
    if (!shouldCycle) {
      if (timerRef.current) clearInterval(timerRef.current);
      return;
    }
    const ms = (autoCycle?.interval ?? 3) * 1000;
    timerRef.current = setInterval(() => {
      setImgIdx((prev) => (prev + 1) % images.length);
    }, ms);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [shouldCycle, images.length, autoCycle?.interval]);

  useEffect(() => { setImgIdx(0); setFailedUrls(new Set()); }, [rv.id]);

  const safeIdx = images.length > 0 ? imgIdx % images.length : 0;
  const currentImg = images.length > 0 ? (images[safeIdx] ?? fallback) : fallback;
  const handleImgError = useCallback(() => {
    const failedSrc = images[safeIdx];
    if (!failedSrc) return;
    setFailedUrls((prev) => { const next = new Set(prev); next.add(failedSrc); return next; });
    setImgIdx(0);
  }, [images, safeIdx]);

  return (
    <article className="overflow-hidden rounded-xl border bg-card shadow-xs">
      {/* Title strip */}
      <div className="border-b px-4 py-2.5">
        <Link
          to={`/product/${rv.slug}`}
          className="block font-heading text-base sm:text-lg font-bold text-foreground hover:text-primary"
        >
          {rv.condition === "new" ? "New " : rv.condition === "used" ? "Used " : ""}
          {rv.title}
        </Link>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Stock #{rv.stock_number}
          {rv.location ? <> · {rv.location}</> : null}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[minmax(220px,300px)_minmax(130px,180px)_minmax(220px,1fr)_minmax(200px,260px)] gap-4 p-4">
        {/* IMAGE */}
        <Link
          to={`/product/${rv.slug}`}
          className="relative block bg-muted rounded-lg overflow-hidden"
          aria-label={rv.title}
        >
          <div className="aspect-[4/3]">
            <CardImage src={currentImg} alt={currentImg === fallback ? "Photos coming soon — representative graphic, not a unit photo" : rv.title} placeholder={currentImg === fallback} priority={priority} onError={handleImgError} />
          </div>
          <div className="absolute left-2 top-2 flex flex-wrap gap-1.5">
            {isSold && (
              <span className="rounded-full bg-destructive px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-destructive-foreground">
                Sold
              </span>
            )}
            {!isSold && rv.condition === "new" && (
              <span className="rounded-full bg-primary px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-primary-foreground">
                New
              </span>
            )}
            {!isSold && rv.condition === "used" && (
              <span className="rounded-full bg-secondary px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-secondary-foreground">
                Used
              </span>
            )}
            {!isSold && (rv as any).certified && (
              <span className="rounded-full bg-amber-500 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white">
                Certified
              </span>
            )}
          </div>
          <WishlistButton stockNumber={rv.stock_number} className="absolute right-2 top-2" />
          <AdminEditButton stockNumber={rv.stock_number} className="absolute left-2 bottom-2" />
        </Link>

        {/* SPECS */}
        <div className="flex flex-col gap-2.5 text-sm">
          {rv.weight_lbs ? (
            <div className="flex items-center gap-2">
              <Scale className="h-4 w-4 text-muted-foreground shrink-0" />
              <span className="font-medium text-foreground">{fmt(rv.weight_lbs)} lbs</span>
            </div>
          ) : null}
          {rv.sleeps ? (
            <div className="flex items-center gap-2">
              <Bed className="h-4 w-4 text-muted-foreground shrink-0" />
              <span className="font-medium text-foreground">Sleeps {rv.sleeps}</span>
            </div>
          ) : null}
          {rv.slides != null ? (
            <div className="flex items-center gap-2">
              <LayoutGrid className="h-4 w-4 text-muted-foreground shrink-0" />
              <span className="font-medium text-foreground">
                {rv.slides} {rv.slides === 1 ? "slide" : "slides"}
              </span>
            </div>
          ) : null}
          {rv.rv_type ? (
            <div className="text-xs text-muted-foreground mt-1">{rv.rv_type}</div>
          ) : null}
        </div>

        {/* FLOORPLAN IMAGE */}
        <Link
          to={`/product/${rv.slug}`}
          className="flex min-h-[150px] items-center justify-center rounded-lg border bg-muted/30 p-2"
          aria-label={`${rv.title} floorplan`}
        >
          {rv.floorplan_image && floorplanSrc ? (
            <ResilientImg
              src={floorplanSrc}
              fallbackSrc={rv.floorplan_image}
              alt={`${rv.title} floorplan`}
              loading="lazy"
              decoding="async"
              className="h-full max-h-[190px] w-full object-contain"
            />
          ) : null}
        </Link>

        {/* PRICE + CTAs */}
        <div className="flex flex-col gap-2 md:border-l md:pl-4">
          {!isSold ? (
            <div className="space-y-1">
              <div className="flex items-baseline justify-between gap-2">
                <span className="font-heading text-sm font-bold text-foreground">Buy Now:</span>
                <span className="font-heading text-lg font-bold text-foreground">
                  ${fmt(displayPrice)}
                </span>
              </div>
              {savings > 0 && rv.price ? (
                <>
                  <div className="flex items-baseline justify-between gap-2 border-t pt-1 text-sm">
                    <span className="text-muted-foreground">Was:</span>
                    <span className="text-muted-foreground line-through">${fmt(rv.price)}</span>
                  </div>
                  <div className="flex items-baseline justify-between gap-2 border-t pt-1 text-sm">
                    <span className="font-semibold text-primary">You Save:</span>
                    <span className="font-semibold text-primary">${fmt(savings)}</span>
                  </div>
                </>
              ) : null}
              <div className="pt-2 text-center text-xs">
                <span className="font-semibold text-primary">Call Today: </span>
                <a href={`tel:${phone}`} className="text-primary hover:underline">
                  {phone}
                </a>
              </div>
            </div>
          ) : (
            <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-center text-sm font-semibold text-destructive">
              Sold
            </div>
          )}

          {!isSold && (
            <div className="mt-1 flex flex-col gap-2">
              {rv.show_best_price_button && (
                <GetLowestPrice
                  title={rv.title}
                  stockNumber={rv.stock_number}
                  year={rv.year}
                  make={rv.make}
                  model={rv.model}
                  variant="prominent"
                  className="w-full rounded-full"
                />
              )}
              <div onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
                <ActionForm
                  title="Make an Offer"
                  displayTitle="Make an Offer"
                  icon={DollarSign}
                  fields={["name", "email", "phone", "offer", "message"]}
                  rv={rv}
                  buttonVariant="default"
                  buttonSize="default"
                  buttonClassName="w-full justify-center gap-2 rounded-full font-heading uppercase"
                />
              </div>
              <Button
                asChild
                size="sm"
                className="w-full rounded-full bg-price-alert hover:bg-price-alert-hover text-price-alert-foreground font-heading uppercase"
              >
                <a href={`tel:${phone}`}>
                  <Bell className="mr-1.5 h-4 w-4" />
                  Price Drop Alert
                </a>
              </Button>
              <Button
                asChild
                variant="outline"
                size="sm"
                className="w-full rounded-full border-primary text-primary hover:bg-primary hover:text-primary-foreground font-heading uppercase"
              >
                <Link to={`/product/${rv.slug}`}>View Details »</Link>
              </Button>
            </div>
          )}
        </div>
      </div>
    </article>
  );
}